/**
 * @file src/pages/invoice-processing-queue/invoiceProgressSlice.js
 * @description Redux slice for real-time invoice processing progress.
 *
 * Responsibilities:
 *  - Track per-invoice processing stages as SSE events arrive
 *  - Provide memoised selectors for components to read invoice state
 *
 * Rules:
 *  - Reducers are PURE functions — no API calls, no async, no side effects
 *  - saveInvoiceProgress API call moved to lib/sseMiddleware.js
 *  - No console.log — selector debug logs removed
 *  - INVOICE_STAGES imported from config/constants — not duplicated here
 *  - initialState uses an empty array — the previous [{invoiceId: null}]
 *    placeholder caused selector mismatches on first render
 */

import { createSlice, createSelector } from "@reduxjs/toolkit";
import { INVOICE_STAGES } from "config/constants";

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState = {
  /**
   * Array of invoice progress records.
   * Shape: {
   *   invoiceId:   string | null,
   *   invoiceName: string,
   *   batchId:     string,
   *   progress:    string[],  ← subset of INVOICE_STAGES
   * }
   */
  invoices: [],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns all INVOICE_STAGES up to and including the given step.
 * Ensures that when an invoice arrives mid-flow (e.g. "validated"),
 * all preceding stages are also marked complete.
 *
 * @param {string} step
 * @returns {string[]}
 */
const getStagesUpTo = (step) => {
  if (!step) return [];
  const index = INVOICE_STAGES.indexOf(step);
  if (index === -1) return [step]; // Unknown stage — keep as-is
  return INVOICE_STAGES.slice(0, index + 1);
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const invoiceProgressSlice = createSlice({
  name: "invoiceSlice",
  initialState,
  reducers: {
    /**
     * Add a new progress step to an existing invoice.
     * Used when a single stage update arrives for a known invoice.
     *
     * @param {{ invoiceId: string, step: string }} action.payload
     */
    addNewProgress(state, action) {
      const { invoiceId, step } = action.payload;

      const invoice = state.invoices.find((inv) => inv.invoiceId === invoiceId);

      if (invoice && !invoice.progress.includes(step)) {
        invoice.progress.push(step);
      }
    },

    /**
     * Add a new invoice or update an existing one with a new stage.
     * Dispatched by sseMiddleware when an SSE event is received.
     *
     * Behaviour:
     *  - New invoice → create record with all stages up to current step
     *  - Existing invoice → append the new step if not already present
     *  - "processed" step → also append "validating" as a UI indicator
     *
     * NOTE: saveInvoiceProgress API call for "submitted" step is handled
     * in lib/sseMiddleware.js — NOT here.
     *
     * @param {{
     *   invoiceId:   string,
     *   invoiceName: string,
     *   batchId:     string,
     *   step:        string,
     * }} action.payload
     */
    addNewInvoice(state, action) {
      const { invoiceId, invoiceName, batchId, step } = action.payload;

      const existing = state.invoices.find(
        (inv) => inv.batchId === batchId && inv.invoiceName === invoiceName,
      );

      if (!existing) {
        // ── New invoice ───────────────────────────────────────────────────
        const progress = getStagesUpTo(step);

        // Add "validating" UI indicator when processing is done
        if (step === "processed" && !progress.includes("validating")) {
          progress.push("validating");
        }

        state.invoices.push({
          invoiceId,
          invoiceName,
          batchId,
          progress,
        });
      } else {
        // ── Existing invoice — update ─────────────────────────────────────

        // Always update invoiceId — it may arrive null on first event
        if (invoiceId) {
          existing.invoiceId = invoiceId;
        }

        if (!existing.progress.includes(step)) {
          existing.progress.push(step);
        }

        // Add "validating" UI indicator after "processed"
        if (step === "processed" && !existing.progress.includes("validating")) {
          existing.progress.push("validating");
        }
      }
    },

    /**
     * Reset all invoice progress — called on batch change or logout.
     */
    resetInvoiceProgress(state) {
      state.invoices = [];
    },
  },

  // Handle the legacy "sse/invoiceUpdate" action type that may exist
  // in older dispatched events — delegates to addNewInvoice reducer
  extraReducers: (builder) => {
    builder.addCase("sse/invoiceUpdate", (state, action) => {
      invoiceProgressSlice.caseReducers.addNewInvoice(state, action);
    });
  },
});

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const { addNewInvoice, addNewProgress, resetInvoiceProgress } =
  invoiceProgressSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Select all invoices in the progress store.
 * @param {object} state - Redux root state
 * @returns {object[]}
 */
export const selectAllInvoices = (state) =>
  state.invoiceReducer?.invoices ?? [];

/**
 * Select a specific invoice by batchId and invoiceName.
 * Memoised with createSelector — only recomputes when invoices array changes.
 *
 * Usage:
 *  const invoice = useSelector((state) =>
 *    selectInvoiceStatusById(state, batchId, invoiceName)
 *  );
 *
 * @param {object} state
 * @param {string} batchId
 * @param {string} invoiceName
 * @returns {object|null}
 */
export const selectInvoiceStatusById = createSelector(
  [
    (state) => state.invoiceReducer?.invoices ?? [],
    (_state, batchId) => batchId,
    (_state, _batchId, invoiceName) => invoiceName,
  ],
  (invoices, batchId, invoiceName) =>
    invoices.find(
      (inv) => inv.batchId === batchId && inv.invoiceName === invoiceName,
    ) ?? null,
);

/**
 * Select all invoices belonging to a specific batch.
 * Memoised — only recomputes when invoices array or batchId changes.
 *
 * @param {object} state
 * @param {string} batchId
 * @returns {object[]}
 */
export const selectInvoicesByBatch = createSelector(
  [
    (state) => state.invoiceReducer?.invoices ?? [],
    (_state, batchId) => batchId,
  ],
  (invoices, batchId) => invoices.filter((inv) => inv.batchId === batchId),
);

/**
 * Select count of invoices that have reached "submitted" stage.
 * Used by the queue overview panel.
 *
 * @param {object} state
 * @returns {number}
 */
export const selectSubmittedCount = createSelector(
  [(state) => state.invoiceReducer?.invoices ?? []],
  (invoices) =>
    invoices.filter((inv) => inv.progress.includes("submitted")).length,
);

export default invoiceProgressSlice.reducer;
